import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ModalContainer from 'components/shared/modal/modal_container';
import RenderInBody from 'components/shared/render_in_body';
import VideoDownloadButton from 'components/shared/UniversalDownloadButton/VideoDownloadButton';
import PostToChannelLinkButton from 'components/shared/PostToChannelLinkButton';
import VideoThumbnail from './VideoThumbnail';
import Skipped from './Skipped';
import SharableRecordingLinkContainer from '../SharableRecordingLinkContainer';
import VideoResponseModal from './Modal/VideoResponseModal';
import { useFeatureFlags } from '../../shared/hooks';

const GradingStationVideoResponse = props => {
  const {
    answer,
    ziggeoApiKey,
    currentUser,
    currentUserId,
    answerCorrect,
    answerFeedback,
    criteriaRatings,
    setAnswerCorrect,
    setAnswerFeedback,
    isAdmin,
  } = props;
  const [modalVisible, setModalVisible] = useState(false);

  const canViewSharableRecordingLink = !useFeatureFlags([
    'hide_sharable_links',
  ]);
  const hasZiggeoForAllMedia = useFeatureFlags(['ziggeo_for_all_media']);

  const hasChannelLearningIntegrationEnabled = useFeatureFlags(
    'readiness-enable-channel-learning-integration',
  );

  const hasDownloadIntegrationEnabled = useFeatureFlags([
    'readiness-enable-seismic-download-video',
  ]);

  const hasChannelsEnableChannels = useFeatureFlags('channels-enable-channels');

  const showPostToChannelLinkButton =
    hasChannelLearningIntegrationEnabled &&
    hasDownloadIntegrationEnabled &&
    hasChannelsEnableChannels;

  const toggleModal = () => {
    setModalVisible(!modalVisible);
  };

  const setCorrect = (_, correct) => {
    setAnswerCorrect(correct);
  };

  const setFeedback = (_, feedback) => {
    setAnswerFeedback(feedback);
  };

  const showSharableRecordingLink =
    answer.givenAnswer &&
    !answer.hasBeenSkipped &&
    canViewSharableRecordingLink;

  const data = {
    ...answer,
    question: answer.questionText,
    preview: false,
    currentUser,
  };

  const isSeismicProvider = answer?.recording?.provider === 'seismic';

  const seismicRecordingUtility = isSeismicProvider && isAdmin;

  return (
    <div className="VideoResponse">
      {answer.hasBeenSkipped && (
        <Skipped
          isCurrentUser={answer.isCurrentUser}
          skipReason={answer.skipReason}
        />
      )}
      {!answer.hasBeenSkipped && (
        <>
          {answer.recording !== null && (
            <button
              type="button"
              className="d-ib w-fill u-resetBtn"
              onClick={toggleModal}
            >
              <VideoThumbnail
                skipped={answer.hasBeenSkipped}
                mediaThumbnailSrc={answer.recording.thumbnail}
                questionType={answer.questionType}
                automatedAnalysis={answer.questionAutomatedAnalysis}
                enableAutomatedAnalysis={answer.enableAutomatedAnalysis}
              />
            </button>
          )}
          {modalVisible && (
            <RenderInBody>
              <ModalContainer
                className="VideoResponseModal ModalTakeover-background Modal-background"
                onEscape={toggleModal}
              >
                <VideoResponseModal
                  data={data}
                  correctAnswer={answerCorrect}
                  currentUserId={currentUserId}
                  feedback={answerFeedback}
                  usePubSub={false}
                  setCorrect={setCorrect}
                  setFeedback={setFeedback}
                  closeModal={toggleModal}
                  modalVisible={modalVisible}
                  criteriaRatings={criteriaRatings}
                  ratingOptions={answer.ratingOptions}
                  apiKey={ziggeoApiKey}
                  canViewSharableRecordingLink={canViewSharableRecordingLink}
                  feedbackCriteriaPresent={
                    answer.criteriaRatings && answer.criteriaRatings.length > 0
                  }
                  hasZiggeoForAllMedia={hasZiggeoForAllMedia}
                  isAdmin={isAdmin}
                  showPostToChannelLinkButton={showPostToChannelLinkButton}
                  automatedAnalysis={answer.questionAutomatedAnalysis}
                  enableAutomatedAnalysis={answer.enableAutomatedAnalysis}
                />
              </ModalContainer>
            </RenderInBody>
          )}
        </>
      )}
      {seismicRecordingUtility && answer.recording !== null && (
        <div className="p-2">
          <div style={{ display: 'inline-flex' }}>
            <VideoDownloadButton
              item={{
                repository: 'Practice',
                type: 'ExternalVideo',
                id: data?.recording?.externalId,
              }}
              isPostToChannels={showPostToChannelLinkButton}
              currentUserId={currentUserId}
            />
            {showPostToChannelLinkButton && (
              <PostToChannelLinkButton
                channelLink={answer.recording.postToChannelLink}
              />
            )}
          </div>
        </div>
      )}
      {showSharableRecordingLink && data.mediaShareLink && (
        <SharableRecordingLinkContainer
          mediaShareLink={answer.mediaShareLink}
        />
      )}
    </div>
  );
};

GradingStationVideoResponse.propTypes = {
  answer: PropTypes.shape({
    position: PropTypes.number,
    questionText: PropTypes.string,
    givenAnswer: PropTypes.string,
    questionMetadata: PropTypes.shape({ message: PropTypes.string }),
    isMediaResponse: PropTypes.bool,
    hasAnswerWithMetadata: PropTypes.bool,
    questionType: PropTypes.string,
    hasQuestionMetadata: PropTypes.bool,
    questionMetadataPresent: PropTypes.bool,
    isChat: PropTypes.bool,
    isGraded: PropTypes.bool,
    questionId: PropTypes.number,
    gradable: PropTypes.bool,
    givenAnswerIsIlos: PropTypes.bool,
    correct: PropTypes.string,
    feedback: PropTypes.string,
    mediaShareLink: PropTypes.string,
    hasBeenSkipped: PropTypes.bool,
    canViewAnswerFeedback: PropTypes.bool,
    id: PropTypes.number,
    userName: PropTypes.string,
    lessonName: PropTypes.string,
    daysAwaitingGrade: PropTypes.number,
    questionAutomatedAnalysis: PropTypes.bool,
    questionAutomatedAnalysisWordsPerMinuteEnabled: PropTypes.bool,
  }),
  ziggeoApiKey: PropTypes.string,
  currentUser: PropTypes.string,
  answerCorrect: PropTypes.string,
  answerFeedback: PropTypes.string,
  criteriaRatings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      criterion_id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      score: PropTypes.number,
    }),
  ),
  setAnswerCorrect: PropTypes.func,
  setAnswerFeedback: PropTypes.func,
};
export default GradingStationVideoResponse;
